/**
 * La frise : tout ce que le niveau donne à jouer, bout à bout, en ticks.
 *
 * Pur : ni React, ni DOM, ni transport, ni pixels. Le dessin est dans
 * `score-layout.ts`, qui choisit seul combien de pixels vaut un tick ; ce qui
 * est ici est le raccord des fenêtres, l'orthographe des notes et la place des
 * noms, où ils se testent. Même partage que `rhythm.ts`, dont ce module lit les
 * fenêtres, et que `staff.ts`, dont il lit le placement vertical.
 *
 * ## Pourquoi une frise, et non plus une fenêtre
 *
 * Le rail dessinait **une** fenêtre d'accord, étirée de bord à bord, et la tête
 * de lecture la parcourait de gauche à droite. On lisait donc un accord à la
 * fois, et l'accord suivant était écrit à côté, dans une marge, avec le mot
 * « après » pour tout raccord. Deux objets pour un seul temps.
 *
 * Une frise continue supprime la marge : ce qui vient est **dessiné plus à
 * droite**, à sa place, à sa distance. Le regard ne change pas d'objet pour
 * anticiper, il regarde plus loin sur le même ruban. C'est aussi ce qui permet
 * de clouer la tête de lecture au milieu et de faire défiler le ruban sous
 * elle : l'œil n'a plus rien à suivre.
 *
 * ## Une fenêtre ne suffit pas à la remplir
 *
 * Une unité du parcours tient souvent **un seul accord** : la frise d'une unité
 * serait donc, la plupart du temps, la fenêtre d'avant sous un autre nom. Elle
 * se construit pour tout le niveau, une fois, et l'écran de jeu y prend la
 * place où il en est.
 *
 * ## Les notes sont écrites, pas seulement situées
 *
 * Une frappe porte ses touches, et chaque touche est **orthographiée** par la
 * fenêtre qui la contient : c'est ce qui décide de la ligne où elle se pose et
 * du signe qu'elle porte devant elle. La fenêtre est le seul endroit où l'on
 * sait s'il faut lire `si♭` ou `la♯`, et c'est aussi elle qui nomme la basse
 * juste au-dessus : les deux viennent donc de la même orthographe, et ne
 * peuvent pas se contredire. Voir `staff.ts`.
 *
 * ## Les noms s'enchaînent, et c'est ce qui leur donne une étendue
 *
 * Un nom vaut jusqu'au suivant : c'est déjà la règle de `laneLabels`, à
 * l'intérieur d'une fenêtre. En frise, la même règle traverse les fenêtres —
 * la dernière basse d'un accord vaut jusqu'à la première du suivant — et cette
 * étendue est exactement ce dont la tête de lecture a besoin pour savoir quel
 * nom lui reste collé.
 */

import {
  beatMarks,
  windowRhythm,
  type RhythmMark,
  type RhythmNote,
  type RhythmSegment,
} from "@/lib/harmony/rhythm";
import { bassLabels, chordLabels, labelLoop, type RailLabel } from "@/lib/harmony/hand-names";
import type { Accidental } from "@/lib/harmony/glyphs";
import { accidentalScribe, type KeySignature } from "@/lib/harmony/keysignature";
import { BASS, TREBLE, spellStaffKey, type StaffKey } from "@/lib/harmony/staff";
import { ottavasOf, shiftAt, writtenStep, type Ottava } from "@/lib/harmony/ottava";
import type { KeyRelation } from "@/lib/harmony/key-spans";

/** Une fenêtre d'accord, réduite à ce que la frise en lit. */
export type FriezeWindow = {
  readonly startTick: number;
  readonly endTick: number;
  readonly left: readonly RhythmNote[];
  readonly right: readonly RhythmNote[];
  /** L'orthographe de la fondamentale, qui décide des bémols et des dièses. */
  readonly rootSpelling: string;
  readonly rootPitchClass: number;
  /**
   * Le nom de l'accord, déjà écrit par l'appelant.
   *
   * Écrit par lui et non ici, parce qu'il faut connaître la notation du joueur,
   * que ce module n'a pas à connaître, et surtout parce que deux endroits qui
   * l'écriraient chacun de leur côté finiraient par diverger.
   *
   * En **chiffrage** et dans la notation des notes, `Ré m` : une partition
   * écrit une grille au-dessus d'elle, et une grille ne fait pas de phrase. Le
   * nom en toutes lettres reste en grand au milieu de l'écran, où la place ne
   * manque pas ; ici une fenêtre d'un temps ne mesure que quatre-vingts pixels,
   * et « Ré mineur » en demande cent cinquante.
   *
   * Dans la notation des notes et non dans celle des symboles d'accord : le
   * chiffrage est lu à quinze pixels des notes qu'il liste, et deux systèmes de
   * noms dans un seul bloc feraient douter des deux. Voir `chordShortLabel`.
   */
  /**
   * Les classes de hauteur que l'accord énonce, ou `null` s'il est illisible.
   *
   * Donnée par l'appelant et non déduite ici : ce module ne voit des notes que
   * leur hauteur et leur instant, jamais la qualité de l'accord qui les porte.
   * Voir `chordPitchClasses`.
   */
  readonly chordPitchClasses: ReadonlySet<number> | null;
  /**
   * Ce que l'analyseur a écrit, touche par touche dans cette fenêtre.
   *
   * Une carte et non une note portée par chaque note : le rythme réduit tout à
   * des numéros de touche avant d'arriver ici, et faire voyager une lettre à
   * travers lui aurait demandé de le rouvrir pour une question qui n'est pas
   * la sienne. Vide sur ce que la fenêtre ne frappe pas — une touche tenue
   * depuis avant, par exemple — et la déduction reprend alors la main.
   */
  readonly spelling: ReadonlyMap<number, string> | null;
  readonly chordName: string;
  /**
   * La tonalité depuis laquelle l'analyseur compte les degrés de cet accord,
   * quand ce n'est pas celle du morceau, et ce qu'elle lui est. Absente ou
   * `null` : la tonalité du morceau. Écrite par l'appelant, comme le nom de
   * l'accord et pour la même raison.
   */
  readonly key?: FriezeKey | null;
  /**
   * Le rang de la position, ou `null` quand on ne la connaît pas.
   *
   * Un rang et non des mots : c'est le dessin qui nomme la position et qui
   * choisit sa couleur, et il a besoin du chiffre pour les deux. Sur **chaque**
   * accord de la frise et non plus sur le seul suivant : le nom ne dit pas la
   * forme sous la main.
   */
  readonly inversion: number | null;
};

/** Une note de l'accord, telle que le nom la liste sous lui. */
export type SpelledNote = {
  /** Dans la notation du joueur, sans octave : la portée dit l'octave. */
  readonly name: string;
  readonly root: boolean;
};

/**
 * Une frappe gravée : ses notes écrites, et jusqu'où la main les tient.
 *
 * C'est le segment de `rhythm.ts` une fois ses touches orthographiées. La
 * séparation est celle du module : le rythme sait *quand* et *quelles touches*,
 * la fenêtre sait *comment ça s'écrit*, et la portée sait *où ça se pose*.
 */
/**
 * Une note gravée : sa place sur la portée, et le signe qui la précède ou rien.
 *
 * Le signe est décidé après coup, quand toute la main est là : il dépend de
 * l'armure, de la mesure, et de ce que la même ligne a déjà porté dans cette
 * mesure-là. Une fonction qui ne voit qu'une note ne peut pas le savoir. Voir
 * `accidentalScribe`.
 */
export type EngravedKey = StaffKey & {
  readonly accidental: Accidental | null;
};

export type FriezeSegment = {
  readonly fromTick: number;
  readonly toTick: number;
  /** Les notes de la frappe, de la plus grave à la plus aiguë. */
  readonly keys: readonly EngravedKey[];
  /**
   * L'orthographe de la fenêtre, gardée pour écrire ce qu'elle ne contient pas.
   *
   * Le joueur enfonce aussi des touches que la partition n'attend pas ici, et
   * l'écran les montre : voir `paintGlows` dans `score-engine.ts`. Il faut alors
   * savoir si `la♯` s'écrit `si♭`, et la réponse est celle que la fenêtre a
   * déjà donnée à ses propres notes — l'accord pour ce qu'il énonce, la
   * tonalité pour le reste, dont fait partie par construction une touche que
   * la partition n'attendait pas. La porter par frappe plutôt que de la
   * redemander au dessin garde une seule orthographe pour toute la fenêtre,
   * celle qui a déjà placé ses notes.
   */
  readonly rootSpelling: string;
  readonly rootPitchClass: number;
  /** Les classes de hauteur que l'accord énonce. Voir `windowToneName`. */
  readonly chordPitchClasses: ReadonlySet<number>;
  /** Ce que l'analyseur a écrit dans cette fenêtre. Voir `windowToneName`. */
  readonly spelling: ReadonlyMap<number, string> | null;
};

/**
 * Un nom posé sur la frise, et l'étendue pendant laquelle il vaut.
 *
 * L'étendue sert au collage : tant que la tête de lecture est dedans, le nom
 * reste sous elle au lieu de défiler avec le ruban. Où exactement il s'arrête
 * dépend de la largeur qu'il occupe une fois rendu, que ce module ne peut pas
 * connaître : le serrage est donc au dessin. Voir `raiseStuck` dans
 * `score-engine.ts`.
 */
export type FriezeLabel = {
  readonly fromTick: number;
  /** Le nom suivant de la voie, ou la fin de la frise. */
  readonly untilTick: number;
  readonly name: string;
  /** Le rang de la position, sur la main droite seulement. */
  readonly inversion: number | null;
  /**
   * La barre du symbole, le « C♯ » de « F♯m/C♯ » : la basse de la frappe
   * quand ce n'est pas la fondamentale. `null` à l'état fondamental, sur une
   * frappe sans main gauche, et sur les noms de la main gauche.
   */
  readonly slash: string | null;
  /**
   * Le degré de la note nommée, ou `null` quand le nom n'en désigne pas une.
   *
   * Il pose le nom **à la hauteur de sa note**, sur la portée, ce que les deux
   * voies d'avant ne permettaient pas. `null` sur les noms d'accord : un accord
   * n'est pas à une hauteur, il en occupe trois, et il s'écrit au-dessus de la
   * portée.
   */
  readonly step: number | null;
  /**
   * Les notes que la main droite pose ici, listées sous le nom de l'accord.
   *
   * Dans l'ordre où elle les empile, donc dans l'ordre du renversement : c'est
   * ce qui fait lire la position au lieu de la faire nommer. Vide sur la main
   * gauche, dont le nom **est** la note.
   */
  readonly spelled: readonly SpelledNote[];
  /**
   * Ce nom se colle-t-il à la tête de lecture ?
   *
   * Faux pour le compte de tours d'une figure répétée : « ×4 » n'est pas un nom
   * qui vaut jusqu'au suivant, c'est une marque posée à l'endroit où la figure
   * recommence. La coller au milieu de l'écran lui ferait dire ce qu'elle ne
   * dit pas.
   */
  readonly sticky: boolean;
};

/** Une tonalité locale, telle que la frise la reçoit. */
export type FriezeKey = {
  /** Le nom écrit pour le joueur : « Ré mineur ». */
  readonly name: string;
  readonly relation: KeyRelation;
};

/**
 * Un changement de tonalité, posé à l'instant où il se produit.
 *
 * Rien sur la page ne le signale quand la tonalité est la relative : même
 * armure, autre tonique. C'est donc une marque de texte, et elle vaut jusqu'au
 * changement suivant ou la fin de la frise. Le retour à la tonalité du morceau
 * est une marque aussi, avec la relation `home`.
 */
export type FriezeKeyMark = {
  readonly tick: number;
  readonly untilTick: number;
  readonly name: string;
  readonly relation: KeyRelation;
};

export type Frieze = {
  readonly fromTick: number;
  readonly toTick: number;
  readonly left: readonly FriezeSegment[];
  readonly right: readonly FriezeSegment[];
  readonly marks: readonly RhythmMark[];
  /** Les noms de touches de la main gauche, dans l'ordre. */
  readonly bass: readonly FriezeLabel[];
  /** Les noms d'accord de la main droite, dans l'ordre. */
  readonly chords: readonly FriezeLabel[];
  /**
   * Les changements de tonalité, dans l'ordre, vide tant que le niveau reste
   * dans la tonalité du morceau. Voir `keyMarksOf`.
   */
  readonly keys: readonly FriezeKeyMark[];
  /**
   * L'orthographe de la tonique, pour écrire une touche hors de toute frappe.
   *
   * Portée par le ruban et non par la frappe, parce qu'elle ne change pas :
   * c'est précisément ce qui la rend utilisable. Voir `windowToneName`.
   */
  readonly keyTonic: string | null;
  /** L'armure des deux portées, que la marge grave à leur tête. */
  readonly signature: KeySignature | null;
  /**
   * La durée d'une mesure, en ticks, ou zéro quand on ne la connaît pas.
   *
   * Le dessin en a besoin pour une raison qui n'a l'air de rien : une
   * altération se grave à gauche de sa note, et une note posée sur le premier
   * temps aurait la sienne de l'autre côté de la barre de mesure, donc dans la
   * mesure d'avant, où elle ne veut rien dire.
   */
  readonly barTicks: number;
  /**
   * Les passages écrits à l'octave, par main, dans l'ordre. Voir `ottava.ts`.
   *
   * Les degrés des frappes et des plaques qu'ils couvrent sont **déjà**
   * décalés : tout ce qui lit `step` lit la hauteur écrite, et seul le crochet
   * dit qu'elle n'est pas celle qu'on entend.
   */
  readonly ottavas: {
    readonly left: readonly Ottava[];
    readonly right: readonly Ottava[];
  };
};

export const EMPTY_FRIEZE: Frieze = {
  fromTick: 0,
  toTick: 0,
  left: [],
  right: [],
  marks: [],
  bass: [],
  chords: [],
  keys: [],
  keyTonic: null,
  signature: null,
  barTicks: 0,
  ottavas: { left: [], right: [] },
};

/**
 * La frise d'un niveau, fenêtre après fenêtre.
 *
 * Les fenêtres sont supposées triées, ce qu'elles sont : ce sont les fenêtres
 * d'accord du niveau, taillées sur le morceau entier et prises dans l'ordre.
 *
 * La main droite s'écrit sur la portée de sol et la gauche sur celle de fa,
 * comme la gravure les monte : c'est sur elles que se décide ce qui s'écrit à
 * l'octave.
 */
export function buildFrieze({
  windows,
  beatTicks,
  beatsPerBar,
  spellNote,
  keyTonic = null,
  signature = null,
  homeKeyName = null,
}: {
  readonly windows: readonly FriezeWindow[];
  /** La durée d'un temps, en ticks. */
  readonly beatTicks: number;
  readonly beatsPerBar: number;
  /**
   * Le nom d'une touche dans la notation du joueur.
   *
   * Passé plutôt que lu : ce module est pur, et la notation est un réglage.
   * Rendu ici et non au dessin parce que la détection de figure répétée
   * compare des noms écrits, et qu'un compte de tours n'est pas une note à
   * réorthographier.
   */
  readonly spellNote: (name: string) => string;
  /**
   * L'orthographe de la tonique du morceau, ou `null` si aucune n'est connue.
   *
   * Une pour tout le ruban, et non une par fenêtre : c'est ce qui la rend
   * stable, et c'est tout ce qu'on lui demande. Voir `windowToneName`.
   */
  readonly keyTonic?: string | null;
  /**
   * L'armure du morceau, ou `null` quand aucune tonalité n'est connue.
   *
   * Elle ne choisit aucune orthographe — `keyTonic` s'en est chargé. Elle
   * décide seulement de ce qu'il reste à **écrire** devant les notes, une fois
   * qu'elle a pris en charge les altérations de la tonalité.
   */
  readonly signature?: KeySignature | null;
  /**
   * Le nom de la tonalité du morceau, écrit pour le joueur, pour marquer le
   * retour à elle après un passage sur une autre. `null` : pas de marque de
   * retour.
   */
  readonly homeKeyName?: string | null;
}): Frieze {
  const first = windows[0];
  const last = windows[windows.length - 1];

  if (first === undefined || last === undefined) {
    return EMPTY_FRIEZE;
  }

  const fromTick = first.startTick;
  const toTick = last.endTick;

  const left: FriezeSegment[] = [];
  const right: FriezeSegment[] = [];
  const bass: Written[] = [];
  const chords: Written[] = [];

  for (const window of windows) {
    const rhythm = windowRhythm({
      startTick: window.startTick,
      endTick: window.endTick,
      left: window.left,
      right: window.right,
      beatTicks,
    });

    // Ce que l'accord énonce, tel que l'appelant l'a lu sur sa qualité. Tout le
    // reste — une note de passage, une broderie, ce que la gauche ajoute sous
    // la basse — est nommé par la tonalité, et ne bouge donc plus d'une fenêtre
    // à l'autre. Un accord dont on ne sait pas lire la qualité ne dit rien, et
    // c'est alors la pile réellement jouée qui sert de dernier recours, comme
    // avant qu'on sache faire mieux.
    const chordPitchClasses: ReadonlySet<number> =
      window.chordPitchClasses ??
      new Set([classOf(window.rootPitchClass), ...window.right.map((note) => classOf(note.midi))]);

    const write = (midi: number) =>
      spellStaffKey({
        midi,
        spelled: window.spelling?.get(midi) ?? null,
        rootSpelling: window.rootSpelling,
        rootPitchClass: window.rootPitchClass,
        chordPitchClasses,
        keyTonic,
      });

    const engrave = (segment: RhythmSegment): FriezeSegment => ({
      fromTick: segment.fromTick,
      toTick: segment.toTick,
      // Sans signe pour l'instant : il faut la main entière pour le décider.
      keys: segment.keys.map((midi) => ({ ...write(midi), accidental: null })),
      rootSpelling: window.rootSpelling,
      rootPitchClass: window.rootPitchClass,
      chordPitchClasses,
      spelling: window.spelling,
    });

    left.push(...rhythm.left.map(engrave));
    right.push(...rhythm.right.map(engrave));

    const hand = {
      gates: rhythm.gates,
      rootSpelling: window.rootSpelling,
      rootPitchClass: window.rootPitchClass,
      chordPitchClasses,
      keyTonic,
      spelling: window.spelling,
    };

    const spelled = bassLabels(hand).map((label) => ({ at: label.at, name: spellNote(label.name) }));

    for (const marked of compacted(spelled)) {
      const gate = rhythm.gates[marked.at];

      if (gate === undefined) {
        continue;
      }

      // La note du dessous, celle que le nom désigne : `gate.left` est
      // croissant, et c'est la même règle que `bassOf`.
      const midi = gate.left[0];

      bass.push({
        tick: gate.tick,
        name: marked.name,
        sticky: marked.sticky,
        inversion: null,
        slash: null,
        step: midi === undefined ? null : write(midi).step,
        spelled: [],
      });
    }

    for (const written of chordLabels(hand, window.chordName)) {
      const gate = rhythm.gates[written.at];

      if (gate !== undefined) {
        // La note du dessous de la gauche, comme pour les noms de basse :
        // `gate.left` est croissant.
        const lowest = gate.left[0];

        chords.push({
          tick: gate.tick,
          name: written.name,
          sticky: true,
          inversion: window.inversion,
          slash:
            lowest === undefined || classOf(lowest) === classOf(window.rootPitchClass)
              ? null
              : spellNote(write(lowest).name),
          step: null,
          spelled: gate.right.map((midi) => ({
            name: spellNote(write(midi).name),
            root: classOf(midi) === classOf(window.rootPitchClass),
          })),
        });
      }
    }
  }

  const barTicks = beatTicks * beatsPerBar;
  const ottavas = {
    left: ottavasOf(left, BASS, barTicks),
    right: ottavasOf(right, TREBLE, barTicks),
  };

  return {
    fromTick,
    toTick,
    left: signed(written(left, ottavas.left), signature, barTicks),
    right: signed(written(right, ottavas.right), signature, barTicks),
    marks: beatMarks({ fromTick, toTick, beatTicks, beatsPerBar }),
    // La plaque d'une touche se pose à la hauteur de sa note, donc à celle où
    // la note s'écrit : sous un 8va, une octave plus bas que ce qu'on entend.
    bass: spanned(
      bass.map((label) =>
        label.step === null ? label : { ...label, step: writtenStep(label.step, shiftAt(ottavas.left, label.tick)) },
      ),
      toTick,
    ),
    chords: spanned(chords, toTick),
    keys: keyMarksOf(windows, homeKeyName, toTick),
    keyTonic,
    signature,
    barTicks,
    ottavas,
  };
}

/**
 * Les frappes d'une main telles qu'elles s'écrivent : une octave plus bas sous
 * un 8va, une octave plus haut sous un 8vb.
 *
 * Avant les altérations et non après : un signe se décide sur la ligne où la
 * note est écrite, et sous un 8va ce n'est plus celle de sa hauteur.
 */
function written(segments: readonly FriezeSegment[], ottavas: readonly Ottava[]): readonly FriezeSegment[] {
  if (ottavas.length === 0) {
    return segments;
  }

  return segments.map((segment) => {
    const shift = shiftAt(ottavas, segment.fromTick);

    if (shift === 0) {
      return segment;
    }

    return { ...segment, keys: segment.keys.map((key) => ({ ...key, step: writtenStep(key.step, shift) })) };
  });
}

/**
 * Les frappes d'une main, chacune avec le signe qu'il faut écrire devant elle.
 *
 * Dans l'ordre des frappes, qui est celui où un lecteur les rencontre, et par
 * main : une altération posée sur une portée ne vaut pas sur l'autre.
 */
function signed(
  segments: readonly FriezeSegment[],
  signature: KeySignature | null,
  barTicks: number,
): FriezeSegment[] {
  const scribe = accidentalScribe(signature);

  return segments.map((segment) => {
    const bar = barTicks > 0 ? Math.floor(segment.fromTick / barTicks) : 0;

    return {
      ...segment,
      keys: segment.keys.map((key) => ({
        ...key,
        accidental: scribe({ bar, step: key.step, alter: key.alter }),
      })),
    };
  });
}

/** Un nom écrit et placé, avant qu'on sache jusqu'où il vaut. */
type Written = {
  readonly tick: number;
  readonly name: string;
  readonly inversion: number | null;
  readonly slash: string | null;
  readonly step: number | null;
  readonly spelled: readonly SpelledNote[];
  readonly sticky: boolean;
};

/** Un nom retenu par la compaction, et la porte où il se pose. */
type Marked = {
  readonly at: number;
  readonly name: string;
  readonly sticky: boolean;
};

/**
 * Ce qui se répète s'écrit une fois, suivi du nombre de tours.
 *
 * La règle est celle du rail d'avant, appliquée fenêtre par fenêtre : une
 * figure de main gauche répétée écrit son motif, puis « ×4 » à l'endroit où
 * elle recommence, et se tait ensuite. Par fenêtre et non sur la frise
 * entière, parce qu'une figure est une manière de tenir **un** accord : la
 * même suite de notes sous deux accords différents n'est pas une répétition,
 * c'est une coïncidence de hauteurs.
 */
function compacted(labels: readonly RailLabel[]): Marked[] {
  const loop = labelLoop(labels);
  const out: Marked[] = [];

  for (let rank = 0; rank < labels.length; rank += 1) {
    const label = labels[rank]!;

    if (loop === null || rank < loop.period) {
      out.push({ at: label.at, name: label.name, sticky: true });

      continue;
    }

    // Le compte se pose là où la figure recommence, donc sur la première
    // étiquette du second tour. Elle marque ainsi la limite du motif en même
    // temps qu'elle le compte. Les tours suivants ne s'écrivent pas.
    if (rank === loop.period) {
      out.push({ at: label.at, name: `×${loop.turns}`, sticky: false });
    }
  }

  return out;
}

/**
 * Chaque nom vaut jusqu'au suivant de sa voie, et le dernier jusqu'au bout.
 *
 * À travers les fenêtres, et c'est le point : la dernière basse d'un accord
 * vaut jusqu'à la première du suivant, quitte à franchir une frontière
 * d'unité. Un nom dont l'étendue s'arrêterait au bord de sa fenêtre laisserait
 * la tête de lecture sans nom collé pendant tout le silence de la main.
 */
function spanned(written: readonly Written[], toTick: number): FriezeLabel[] {
  return written.map((label, rank) => ({
    fromTick: label.tick,
    untilTick: written[rank + 1]?.tick ?? toTick,
    name: label.name,
    inversion: label.inversion,
    slash: label.slash,
    step: label.step,
    spelled: label.spelled,
    sticky: label.sticky,
  }));
}

/**
 * Le nom qui vaut à cet instant, ou `null` avant le premier.
 *
 * Étendue fermée à gauche et ouverte à droite : à l'instant précis où un nom
 * s'éteint, c'est le suivant qui vaut, et non les deux. Sans quoi deux noms se
 * disputeraient le milieu de l'écran à chaque changement d'accord.
 */
export function activeLabel(
  labels: readonly FriezeLabel[],
  headTick: number,
): FriezeLabel | null {
  for (const label of labels) {
    if (label.sticky && label.fromTick <= headTick && headTick < label.untilTick) {
      return label;
    }
  }

  return null;
}

/**
 * Les ticks où le dessin de la frise change de contenu.
 *
 * ## À quoi ça sert
 *
 * La partition lit maintenant sa position sur le studio, image par image, et
 * l'écrit directement sur le nœud du ruban. Mais tout ce qu'elle dessine ne
 * bouge pas à l'image : quel nom est collé à la tête, quelles notes sont
 * derrière elle, quelle part de la frise vaut la peine d'être gravée, tout
 * cela ne change qu'à des instants précis. Repasser par React soixante fois
 * par seconde pour redessiner plusieurs centaines de nœuds identiques serait
 * le contraire de ce que la lecture par image doit coûter.
 *
 * Ces instants-là sont ceux-ci : chaque temps de la règle, chaque début de
 * nom, chaque attaque de chaque main. La tête les franchit, et c'est **là**
 * qu'elle se promeut en état React. Entre deux, elle ne fait que glisser.
 *
 * Les temps y sont, en plus des attaques et des noms, et ce n'est pas du
 * luxe : la fenêtre de gravure est centrée sur la position promue, et une
 * frise dont on ne connaîtrait que les attaques laisserait cette fenêtre
 * immobile pendant les longues tenues. Un temps est aussi la plus petite
 * distance qui vaille un rendu.
 */
export function friezeBoundaries(frieze: Frieze): readonly number[] {
  const ticks = new Set<number>([frieze.fromTick]);

  for (const mark of frieze.marks) {
    ticks.add(mark.tick);
  }

  for (const label of [...frieze.bass, ...frieze.chords]) {
    ticks.add(label.fromTick);
  }

  for (const segment of [...frieze.left, ...frieze.right]) {
    ticks.add(segment.fromTick);
  }

  return [...ticks].filter((tick) => tick <= frieze.toTick).sort((a, b) => a - b);
}

/**
 * La tête à porter dans le rendu, ou `null` quand rien n'a changé du dessin.
 *
 * Le tick exact et non la borne : c'est lui qui dit de quel côté de sa note la
 * tête se trouve, et c'est toute la différence entre « posée dessus » et
 * « passée dessus ». Une note s'allume au second, jamais au premier, sinon le
 * premier accord d'une fenêtre paraîtrait joué à l'instant où l'on y arrive.
 *
 * Deux raisons de porter, et il en faut deux :
 *
 * - la tête a franchi une borne, donc le dessin change ;
 * - la tête **quitte** la borne où elle était posée. Sans ce second cas, une
 *   note ne s'allumerait qu'à la borne suivante : on la jouerait, la tête
 *   partirait, et elle resterait éteinte jusqu'au temps d'après.
 *
 * Le second ne coûte qu'un rendu de plus par arrêt, puisqu'il ne peut se
 * produire que depuis une position posée exactement sur une borne.
 */
export function promotedHead(
  boundaries: readonly number[],
  current: number,
  tick: number,
): number | null {
  if (boundaryAtTick(boundaries, tick) !== boundaryAtTick(boundaries, current)) {
    return tick;
  }

  if (tick !== current && current === boundaryAtTick(boundaries, current)) {
    return tick;
  }

  return null;
}

/**
 * Le dernier de ces instants que la tête a franchi.
 *
 * Borné aux deux bouts plutôt que rendu tel quel : hors de la frise il n'y a
 * rien de gravé, donc rien à recalculer, et rendre le tick brut ferait un
 * rendu par image pendant tout ce que le morceau contient avant le niveau.
 *
 * Par dichotomie, à la différence de `chordIndexAtTick` qui parcourt : une
 * unité tient une dizaine d'accords, une frise de niveau tient un temps par
 * temps du morceau, et celle-ci est interrogée à chaque image.
 */
export function boundaryAtTick(boundaries: readonly number[], tick: number): number {
  const first = boundaries[0];

  if (first === undefined || tick <= first) {
    return first ?? 0;
  }

  let low = 0;
  let high = boundaries.length - 1;

  while (low < high) {
    const middle = Math.ceil((low + high) / 2);

    if (boundaries[middle]! <= tick) {
      low = middle;
    } else {
      high = middle - 1;
    }
  }

  return boundaries[low]!;
}

function classOf(midi: number): number {
  return ((midi % 12) + 12) % 12;
}

/** Les degrés de toutes les notes d'une main, pour choisir son étendue. */
export function friezeSteps(segments: readonly FriezeSegment[]): number[] {
  return segments.flatMap((segment) => segment.keys.map((key) => key.step));
}

/**
 * La marque de tonalité sous la tête, ou `null`.
 *
 * Seulement hors de la tonalité du morceau : c'est là que la partition ne dit
 * rien et que le nom doit rester sous les yeux. Le retour chez soi se lit en
 * passant, dans le ruban, et n'a pas à suivre la tête jusqu'à la fin.
 */
export function activeKeyMark(marks: readonly FriezeKeyMark[], headTick: number): FriezeKeyMark | null {
  for (const mark of marks) {
    if (mark.relation !== "home" && mark.tick <= headTick && headTick < mark.untilTick) {
      return mark;
    }
  }

  return null;
}

/**
 * Les changements de tonalité, lus fenêtre par fenêtre.
 *
 * Une marque là où la tonalité d'une fenêtre diffère de celle de la fenêtre
 * précédente ; la première fenêtre ne marque que si elle est déjà hors de la
 * tonalité du morceau, puisque rester chez soi ne se signale pas. Le retour
 * chez soi est marqué au nom du morceau quand on le connaît, sinon tu. Vide
 * quand rien ne quitte la tonalité du morceau.
 */
export function keyMarksOf(
  windows: readonly Pick<FriezeWindow, "startTick" | "key">[],
  homeKeyName: string | null,
  toTick: number,
): FriezeKeyMark[] {
  const marks: FriezeKeyMark[] = [];
  let previous: FriezeKey | null = null;

  windows.forEach((window, index) => {
    const current = window.key ?? null;
    const same =
      current === previous ||
      (current !== null && previous !== null && current.name === previous.name && current.relation === previous.relation);

    if (same) {
      return;
    }

    previous = current;

    if (current === null) {
      if (index > 0 && homeKeyName !== null) {
        marks.push({ tick: window.startTick, untilTick: toTick, name: homeKeyName, relation: "home" });
      }

      return;
    }

    marks.push({ tick: window.startTick, untilTick: toTick, name: current.name, relation: current.relation });
  });

  if (marks.every((mark) => mark.relation === "home")) {
    return [];
  }

  return marks.map((mark, index) => ({
    ...mark,
    untilTick: marks[index + 1]?.tick ?? toTick,
  }));
}
